/**
 * useTutorLiveCenterAnnotation — dessiner sur ce que le masteur présente.
 *
 * Le port de `common/hooks/useAnnotationState.js` et de ce que `TodoModal` en faisait (branche `noModal`) :
 *  - **un calque par surface** — l'énoncé, le rendu, le tableau blanc —, ses traits gardés **par activité**
 *    pendant la présentation (revenir sur une activité les redessine) ;
 *  - à l'arrêt de la présentation, le crayon s'éteint et les traits partent, **sauf ceux du tableau blanc**,
 *    qui reviennent à la présentation suivante ;
 *  - « Annuler » vise le calque de l'onglet (le tableau, le dernier touché en côte à côte, le rendu, sinon
 *    l'énoncé) ; « Tout effacer » de même (les deux en côte à côte) ; tous deux grisés tant que l'activité
 *    n'a aucun trait ;
 *  - au web, pendant la présentation et hors d'un champ de saisie : P dessine, Ctrl/⌘+Z annule (pas avec
 *    Maj), Suppr efface tout, Échap arrête de dessiner (`TodoModal:1284-1343`).
 *
 * Ce qui s'ajoute (round 3, `live.md` § 7 n° 10) : l'encre (quatre jetons) et la gomme, partagées par la
 * barre de la présentation et celle du tableau blanc ; hors présentation, on dessine au tableau blanc (sa
 * barre y est posée), et seulement là, comme l'ancien n'offrait le calque qu'en présentation ailleurs.
 *
 * Un écart : le rendu a un calque pour la copie, pas un par page (le design system ne dit pas la page
 * tournée à l'écran) ; dessiner fige la page comme avant (le calque prend le doigt).
 */
import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
    annotationSteps,
    clearAnnotation,
    createAnnotationStore,
    undoAnnotation,
    type AnnotationStore,
} from "../../../../../../design-system/molecules/AnnotationCanvas/annotationStore"
import {
    type TutorAnnotationPen,
    type TutorInk,
} from "../../../../../../design-system/molecules/TutorAnnotationToolbar/TutorAnnotationToolbar"
import { type TutorLiveDrawSurface } from "../../../../../../design-system/organisms/TutorLiveActivityView/TutorLiveActivityView"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { renderAnnotationCanvas } from "./useTutorLiveCenterSurfaces"

/** La vue que montre le centre, qui dit le calque visé par « Annuler » et « Tout effacer ». */
export type AnnotationView = "statement" | "work" | "split" | "whiteboard" | "other"

const SURFACES: TutorLiveDrawSurface[] = ["statement", "work", "whiteboard"]

/** Le calque que vise « Annuler » (`TodoModal`, le crayon de la barre et Ctrl+Z). */
export function undoTarget(
    view: AnnotationView,
    lastTouched: TutorLiveDrawSurface | null
): TutorLiveDrawSurface | null {
    switch (view) {
        case "whiteboard":
            return "whiteboard"
        case "split":
            return lastTouched === "work" || lastTouched === "statement" ? lastTouched : null
        case "work":
            return "work"
        case "statement":
            return "statement"
        case "other":
            return null
    }
}

/** Les calques que vide « Tout effacer » : les deux en côte à côte. */
export function clearTargets(view: AnnotationView): TutorLiveDrawSurface[] {
    switch (view) {
        case "whiteboard":
            return ["whiteboard"]
        case "split":
            return ["statement", "work"]
        case "work":
            return ["work"]
        case "statement":
            return ["statement"]
        case "other":
            return []
    }
}

/** La touche d'un raccourci, hors d'un champ de saisie (`TodoModal:1291-1340`). */
export type AnnotationShortcut = "draw" | "undo" | "clear" | "stopDraw" | null

export function annotationShortcut(event: {
    key: string
    ctrlKey?: boolean
    metaKey?: boolean
    shiftKey?: boolean
    target?: { tagName?: string } | null
}): AnnotationShortcut {
    const tag = event.target?.tagName
    if (tag === "INPUT" || tag === "TEXTAREA") return null
    if (event.key === "p" || event.key === "P") return "draw"
    if (event.key === "z" && (event.ctrlKey === true || event.metaKey === true) && event.shiftKey !== true)
        return "undo"
    if (event.key === "Delete") return "clear"
    if (event.key === "Escape") return "stopDraw"
    return null
}

export type TutorLiveCenterAnnotation = {
    /** La barre de dessin de « Présenter ». */
    toolbar: {
        drawing: boolean
        canUndo: boolean
        onToggleDraw: () => void
        onUndo: () => void
        onClear: () => void
        pen: TutorAnnotationPen
    }
    /** La barre du tableau blanc, hors présentation. */
    whiteboard: { pen: TutorAnnotationPen; canUndo: boolean; onUndo: () => void; onClear: () => void }
    /** Le calque d'une surface (la fente `annotation`). */
    annotation: (surface: TutorLiveDrawSurface) => React.ReactNode
}

export function useTutorLiveCenterAnnotation(args: {
    activityId: string | null
    presenting: boolean
    view: AnnotationView
}): TutorLiveCenterAnnotation {
    const { activityId, presenting, view } = args
    const storesRef = useRef<Map<string, AnnotationStore>>(new Map())
    const [drawing, setDrawing] = useState(false)
    const [ink, setInk] = useState<TutorInk>("role")
    const [version, setVersion] = useState(0)
    const lastTouchedRef = useRef<TutorLiveDrawSurface | null>(null)
    const bump = useCallback(() => setVersion((n) => n + 1), [])

    const storeOf = useCallback(
        (surface: TutorLiveDrawSurface) => {
            const key = `${activityId}_${surface}`
            let store = storesRef.current.get(key)
            if (store == null) {
                store = createAnnotationStore()
                storesRef.current.set(key, store)
            }
            return store
        },
        [activityId]
    )

    // À l'arrêt de la présentation : le crayon s'éteint, les traits partent, ceux du tableau blanc restent.
    useEffect(() => {
        if (presenting) return
        setDrawing(false)
        for (const key of [...storesRef.current.keys()]) {
            if (!key.endsWith("_whiteboard")) storesRef.current.delete(key)
        }
        bump()
    }, [presenting, bump])

    // Une autre activité : ses calques se redessinent (ses traits gardés), le dernier touché s'oublie.
    useEffect(() => {
        lastTouchedRef.current = null
        bump()
    }, [activityId, bump])

    const steps = SURFACES.reduce(
        (sum, surface) => sum + annotationSteps(storesRef.current.get(`${activityId}_${surface}`)),
        0
    )
    // `version` fait relire le compte après un trait.
    void version

    const undo = useCallback(() => {
        const target = undoTarget(view, lastTouchedRef.current)
        if (target == null) return
        if (undoAnnotation(storeOf(target))) bump()
    }, [view, storeOf, bump])
    const clear = useCallback(() => {
        for (const surface of clearTargets(view)) clearAnnotation(storeOf(surface))
        bump()
    }, [view, storeOf, bump])

    // Les raccourcis du clavier, au web, pendant la présentation.
    const actionsRef = useRef({ undo, clear })
    actionsRef.current = { undo, clear }
    useEffect(() => {
        if (!IS_WEB || !presenting || typeof document === "undefined") return
        const onKeyDown = (event: KeyboardEvent) => {
            const shortcut = annotationShortcut({
                key: event.key,
                ctrlKey: event.ctrlKey,
                metaKey: event.metaKey,
                shiftKey: event.shiftKey,
                target: event.target as { tagName?: string } | null,
            })
            if (shortcut == null) return
            if (shortcut !== "stopDraw") event.preventDefault()
            if (shortcut === "draw") setDrawing(true)
            else if (shortcut === "undo") actionsRef.current.undo()
            else if (shortcut === "clear") actionsRef.current.clear()
            else setDrawing(false)
        }
        document.addEventListener("keydown", onKeyDown)
        return () => document.removeEventListener("keydown", onKeyDown)
    }, [presenting])

    const pen = useMemo<TutorAnnotationPen>(() => ({ ink, onChangeInk: setInk }), [ink])

    const annotation = useCallback(
        (surface: TutorLiveDrawSurface): React.ReactNode => {
            // Hors présentation, seul le tableau blanc prend le trait (sa barre y est posée).
            if (!presenting && surface !== "whiteboard") return null
            if (activityId == null) return null
            return renderAnnotationCanvas({
                store: storeOf(surface),
                enabled: presenting ? drawing : true,
                ink,
                version,
                onChange: () => {
                    lastTouchedRef.current = surface
                    bump()
                },
            })
        },
        [presenting, activityId, storeOf, drawing, ink, version, bump]
    )

    return {
        toolbar: {
            drawing,
            canUndo: steps > 0,
            onToggleDraw: () => setDrawing((on) => !on),
            onUndo: undo,
            onClear: clear,
            pen,
        },
        whiteboard: { pen, canUndo: steps > 0, onUndo: undo, onClear: clear },
        annotation,
    }
}
